import SEO from '../components/common/SEO';
import { Link } from 'react-router-dom';
import { Building2, ChartNoAxesCombined, CircleCheckBig, ArrowRight, ShieldCheck, Sparkles, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { isBusinessRegistrationBlockedRole } from '../utils/businessRegistrationAccess';
import SectionShell from '../components/home/SectionShell';
import EnterpriseFeatureCard from '../components/home/EnterpriseFeatureCard';
import StatPill from '../components/home/StatPill';
import ProcessStep from '../components/home/ProcessStep';
import TrustBar from '../components/home/TrustBar';
import CaseStudyStrip from '../components/home/CaseStudyStrip';
import ReviewQualityFramework from '../components/home/ReviewQualityFramework';
import FAQAccordion from '../components/home/FAQAccordion';

const platformHighlights = [
  {
    icon: ShieldCheck,
    title: 'Verified Trust Layer',
    description:
      'Review visibility is aligned to validation signals so customers can prioritize credibility over noise.',
  },
  {
    icon: ChartNoAxesCombined,
    title: 'Decision-Ready Insights',
    description:
      'Structured scoring and category-level trends help users compare businesses with confidence.',
  },
  {
    icon: Building2,
    title: 'Business Reputation Console',
    description:
      'Enterprise-ready tools help owners manage perception, respond faster.',
  },
];

const whyChooseVMC = [
  {
    title: 'For Consumers',
    description: 'Make informed decisions with 4.8/5 Average Trust Score and avoid unreliable feedback.',
  },
  {
    title: 'For Businesses',
    description: 'Move beyond scattered reviews. Gain an enterprise-ready tool to build a reputation that drives growth.',
  },
  {
    title: 'For Everyone',
    description: 'Participate in a fair, transparent ecosystem where quality feedback is rewarded.',
  },
];

const consumerBenefits = [
  {
    title: 'See What\'s Real',
    description: 'Our 95% Review Authenticity score means you can focus on feedback from verified consumers.',
  },
  {
    title: 'Compare with Confidence',
    description: 'Use decision-ready insights like category-level trends and recency indicators to pick the right pro for the job.',
  },
  {
    title: 'Share Your Experience',
    description: 'Help build a stronger community by posting your own trusted reviews after you\'ve used a service.',
  },
];

const businessBenefits = [
  {
    title: 'Get Listed & Found',
    description: 'Join 1,900+ registered businesses and make your profile visible to active consumers searching for your services.',
  },
  {
    title: 'Build Unshakeable Trust',
    description: 'Showcase your commitment to quality with our Verified Trust Layer. Authentic, structured reviews build more credibility than scattered testimonials.',
  },
  {
    title: 'Manage Your Profile',
    description: 'Use your Business Reputation Console to understand customer sentiment, respond to feedback, and highlight what makes you great.',
  },
];

const workflow = [
  {
    number: '01',
    title: 'Businesses List',
    description: 'Create a detailed profile to start building your verified presence.',
  },
  {
    number: '02',
    title: 'Consumers Search & Review',
    description: 'Find businesses and share structured feedback on their experience.',
  },
  {
    number: '03',
    title: 'Trust Signals Guide Decisions',
    description: 'Our platform verifies identity and moderates content, publishing clear signals that help everyone make smarter choices.',
  },
];

const caseStudies = [
  {
    company: 'NorthBridge Services',
    sector: 'Local Services Network',
    metric: '+27%',
    metricLabel: 'Profile conversion uplift',
    summary: 'Moved from fragmented social proof to structured VMC reviews and improved lead quality in under one quarter.',
    iconKey: 'growth',
  },
  {
    company: 'UrbanCare Clinics',
    sector: 'Healthcare Provider Group',
    metric: '2.1x',
    metricLabel: 'Response consistency',
    summary: 'Using dashboard workflows and transparent public profiles increased customer trust and service follow-up quality.',
    iconKey: 'response',
  },
  {
    company: 'PrimeBuild Partners',
    sector: 'Construction and Contracting',
    metric: '-38%',
    metricLabel: 'Decision cycle time',
    summary: 'Prospects made faster decisions after seeing clear review credibility and verified business identity signals.',
    iconKey: 'speed',
  },
];

const reviewQualityFramework = [
  {
    // label: 'Step 01',
    title: 'Collect Structured Feedback',
    description: 'Reviews capture standardized fields so quality, recency, and trust signals can be analyzed consistently.',
    iconKey: 'collect',
  },
  {
    // label: 'Step 02',
    title: 'Verify Identity Signals',
    description: 'Verification markers help differentiate trusted reviewer activity from low-confidence or anonymous noise.',
    iconKey: 'verify',
  },
  {
    // label: 'Step 03',
    title: 'Moderate with Clear Rules',
    description: 'Approval and quality checks ensure policy alignment before visibility changes impact public reputation.',
    iconKey: 'moderate',
  },
  {
    // label: 'Step 04',
    title: 'Publish Transparent Signals',
    description: 'Users see understandable trust indicators that support faster decisions and higher platform confidence.',
    iconKey: 'publish',
  },
];

const faqs = [
  {
    question: 'How does VMC reduce fake or low-quality reviews?',
    answer:
      'VMC prioritizes verification and moderation signals in how feedback is surfaced, helping users focus on trustworthy review content.',
  },
  {
    question: 'Can businesses improve their trust profile over time?',
    answer:
      'Yes. By responding consistently, maintaining profile quality, and earning stronger verified feedback, businesses improve credibility and conversion.',
  },
  {
    question: 'Is this platform only for public listing or also operations?',
    answer:
      'Both. VMC is designed for public discovery and internal reputation operations, which is why dashboards and review workflows are central.',
  },
  {
    question: 'Why does the homepage emphasize structured signals?',
    answer:
      'Enterprise buyers and informed consumers make faster decisions when quality, recency, and verification are clearly represented.',
  },
];

const HomePage = () => {
  const { isAuthenticated, user } = useAuth();
  const canShowBusinessRegistrationLink = !isAuthenticated || !isBusinessRegistrationBlockedRole(user?.role);

  return (
    <div className="relative overflow-hidden bg-slate-50">
      <SEO
        title="VMC | Transparent Business Listings & Verified Customer Reviews"
        description="Discover trusted local businesses on VMC. Read verified customer reviews, compare quality signals, and list your company to build reputation with structured feedback."
      />
      <div className="home-aurora-bg absolute inset-0 pointer-events-none" />

      <div className="relative">
        <section className="container-custom pt-14 pb-12 sm:pt-20 sm:pb-16">
          <div className="home-reveal rounded-3xl border border-cyan-100/70 bg-white/80 px-6 py-10 shadow-xl shadow-cyan-100/50 backdrop-blur-sm sm:px-10 sm:py-14">
            <div className="grid items-center gap-8 lg:grid-cols-[1.2fr_0.8fr]">
              <div>
                <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-800">
                  <Sparkles size={14} />
                  Enterprise Review Intelligence
                </span>
                <h1 className="text-4xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-5xl">
                  Find Trusted Businesses, Backed by Verified Reviews.
                </h1>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                  Stop guessing. VMC connects you with US businesses through transparent ratings and authentic feedback, so you can choose with confidence.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link
                    to="/businesses"
                    className="inline-flex items-center rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-700"
                  >
                    <ArrowRight size={16} className="mr-2" />
                    Search for a Business
                  </Link>
                  {canShowBusinessRegistrationLink && (
                    <Link
                      to="/register-business"
                      className="inline-flex items-center rounded-xl border border-cyan-200 bg-white px-6 py-3 text-sm font-semibold text-cyan-800 transition-all duration-200 hover:-translate-y-0.5 hover:bg-cyan-50"
                    >
                      <ArrowRight size={16} className="mr-2" />
                      List Your Business
                    </Link>
                  )}
                </div>
              </div>

              <div className="home-reveal-delay rounded-2xl border border-slate-200 bg-slate-900/95 p-6 text-slate-100 shadow-lg">
                <div className="mb-6 flex items-center gap-2 text-cyan-300">
                  <CircleCheckBig size={18} />
                  <p className="text-xs mb-1 font-semibold uppercase tracking-[0.15em]">Marketplace Confidence</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <StatPill value="95%" label="Review Authenticity" />
                  <StatPill value="4.8/5" label="Average Trust Score" />
                  <StatPill value="12K+" label="Active Consumers" />
                  <StatPill value="1.9K+" label="Registered Businesses" />
                </div>

                {/* <div className="mt-6 rounded-xl border border-slate-700 bg-slate-800/80 p-4">
                  <p className="text-sm leading-relaxed text-slate-300">
                    Your project combines public discoverability with business operations. This homepage reflects that dual
                    purpose with a clear trust-first enterprise experience.
                  </p>
                </div> */}
              </div>
            </div>
          </div>
        </section>

        <SectionShell
          bgClass="bg-[#FFFFFF]"
          eyebrow="Platform Strengths"
          title="Built for consumers, operators, and growing business teams"
          subtitle="Reusable components power this layout so the same design language can be extended to other landing and product pages."
          align="center"
        >
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {platformHighlights.map((feature, index) => (
              <div key={feature.title} className={index === 1 ? 'home-reveal home-reveal-delay h-full' : 'home-reveal h-full'}>
                <EnterpriseFeatureCard {...feature} />
              </div>
            ))}
          </div>
        </SectionShell>

        <SectionShell
          bgClass="bg-[#F7F8FA]"
          eyebrow="Why Choose VMC"
          title="Built for every participant in the marketplace"
          subtitle="Whether you're searching, listing, or building trust—VMC delivers distinct value for consumers, businesses, and the ecosystem."
          align="center"
        >
          <div className="grid gap-5 md:grid-cols-3">
            {whyChooseVMC.map((item, index) => (
              <div key={item.title} className={index === 1 ? 'home-reveal home-reveal-delay h-full' : 'home-reveal h-full'}>
                <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <h3 className="mb-3 text-lg font-semibold text-slate-900">{item.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionShell>

        <SectionShell
          bgClass="bg-[#FFFFFF]"
          eyebrow="For Consumers"
          title="Find Your Next Trusted Service"
          subtitle="Search through thousands of listed US businesses across every category. With VMC, you don't just see a star rating—you see the full picture."
          align="center"
        >
          <div className="grid gap-5 md:grid-cols-3">
            {consumerBenefits.map((benefit, index) => (
              <div key={benefit.title} className={index === 1 ? 'home-reveal home-reveal-delay h-full' : 'home-reveal h-full'}>
                <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <h3 className="mb-3 text-lg font-semibold text-slate-900">{benefit.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionShell>

        <SectionShell
          bgClass="bg-[#F7F8FA]"
          eyebrow="For Businesses"
          title="Turn Reviews into Reputation"
          subtitle="Your reputation is your most valuable asset. VMC gives you the tools to manage it proactively and attract more customers."
          align="center"
        >
          <div className="grid gap-5 md:grid-cols-3">
            {businessBenefits.map((benefit, index) => (
              <div key={benefit.title} className={index === 1 ? 'home-reveal home-reveal-delay h-full' : 'home-reveal h-full'}>
                <div className="flex h-full flex-col items-center justify-center rounded-2xl border border-cyan-200 bg-white p-6 text-center shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md">
                  <h3 className="mb-3 text-lg font-semibold text-slate-900">{benefit.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionShell>

        <SectionShell
          bgClass="bg-[#F7F8FA]"
          eyebrow="How VMC Works"
          title="A Transparent Feedback Loop"
          subtitle="Businesses list, consumers review, and trust signals guide decisions—creating a marketplace where transparency drives confidence."
          align="center"
        >
          <div className="grid gap-5 md:grid-cols-3">
            {workflow.map((step, index) => (
              <div key={step.number} className={index === 2 ? 'home-reveal home-reveal-delay h-full' : 'home-reveal h-full'}>
                <ProcessStep {...step} />
              </div>
            ))}
          </div>
        </SectionShell>

        {/* Case Study Section - Commented Out */}
        {/* <SectionShell
          bgClass="bg-[#FFFFFF]"
          eyebrow="Outcomes"
          title="Trusted review operations produce measurable business impact"
          subtitle="These representative outcomes show how structured reputation systems influence conversion quality and customer confidence."
        >
          <CaseStudyStrip studies={caseStudies} />
        </SectionShell> */}

        <SectionShell
          bgClass="bg-[#F7F8FA]"
          eyebrow="Review Quality Framework"
          title="How VMC turns feedback into trusted decision signals"
          subtitle="A clear quality pipeline ensures review integrity before signals influence public perception and business outcomes."
          align="center"
        >
          <ReviewQualityFramework steps={reviewQualityFramework} />
        </SectionShell>

        <SectionShell
          bgClass="bg-[#FFFFFF]"
          eyebrow="Frequently Asked"
          title="Frequntly Asked Questions" 
          subtitle="Clear answers for both business operators and consumers evaluating trust infrastructure."
          align="center"
        >
          <FAQAccordion faqs={faqs} />
        </SectionShell>

        <SectionShell bgClass="bg-[#F7F8FA]" align="center">
          <div className="home-reveal rounded-3xl border border-cyan-100 bg-gradient-to-r from-cyan-50 via-white to-teal-50 px-6 py-10 shadow-sm sm:px-10">
            <div className="mx-auto max-w-2xl">
              <div className="flex justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-cyan-700">
                  <Users size={14} />
                  Start Growing Trust
                </span>
              </div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl text-center">
                Ready to find your next trusted service or grow your business's reputation?
              </h2>
              <div className="mt-7 flex flex-wrap justify-center gap-3">
                <Link
                  to="/businesses"
                  className="inline-flex items-center rounded-xl bg-cyan-700 px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-cyan-800"
                >
                  <ArrowRight size={16} className="mr-2" />
                  Search Now
                </Link>
                <Link
                  to="/register-business"
                  className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-700 transition-all duration-200 hover:-translate-y-0.5 hover:bg-slate-50"
                >
                  <ArrowRight size={16} className="mr-2" />
                  List Your Company
                </Link>
              </div>
            </div>
          </div>
        </SectionShell>
      </div>
    </div>
  );
};

export default HomePage;
